/**
 * Price helpers — single source of truth for the whole app (frontend input, save layer).
 *
 * Mirrors the same logic used by the Edge Functions in supabase/functions/_shared/menu-engine.ts
 * (kept in sync intentionally; Deno edge code cannot import from this package).
 */

/**
 * Real-time input sanitizer for the PRICE field. Keeps ONLY digits and a SINGLE decimal separator
 * (',' or '.'), dropping letters, ranges ('-'), currency, spaces and any 2nd separator. The first
 * separator typed wins. Use in the input onChange so invalid characters never appear and the field
 * can only ever hold a single number. Empty string is allowed (in-progress typing).
 *   "5a"->"5" · "5-10"->"510" · "5,,5"->"5,5" · "12€"->"12" · "abc"->""
 */
export function sanitizePriceInput(raw: string | null | undefined): string {
    if (raw == null) return '';
    let out = '';
    let sepUsed = '';
    for (const ch of String(raw)) {
        if (ch >= '0' && ch <= '9') { out += ch; continue; }
        if (ch === ',' || ch === '.') {
            if (sepUsed === '') { sepUsed = ch; out += ch; }
        }
    }
    return out;
}

/**
 * Normalize a DISPLAY price for storage: strip currency symbols/codes but KEEP the user's decimal
 * separator untouched (numeric math is done via price_cents). "5,50€" -> "5,50".
 */
export function normalizeDisplayPrice(priceStr: string | null | undefined): string {
    if (priceStr === null || priceStr === undefined) return '';
    let s = String(priceStr).trim();
    if (s === '') return '';
    s = s.replace(/[€$£¥]/g, '');
    s = s.replace(/^\s*(EUR|USD|GBP|JPY|CHF)\s*/i, '').replace(/\s*(EUR|USD|GBP|JPY|CHF)\s*$/i, '');
    return s.trim();
}

/**
 * Parse a price string to integer cents, or null if unparseable. Handles both decimal conventions
 * and thousands separators:
 *   "5.50"/"5,50" -> 550 · "1.234,56" (EU) -> 123456 · "1,234.56" (US) -> 123456 · "1.234" -> 1234
 */
export function priceToCents(priceStr: string | null | undefined): number | null {
    if (priceStr === null || priceStr === undefined) return null;
    let s = String(priceStr).replace(/[^0-9.,]/g, '');
    if (!s || s === '.' || s === ',') return null;

    const hasDot = s.includes('.');
    const hasComma = s.includes(',');
    if (hasDot && hasComma) {
        if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
            s = s.replace(/\./g, '').replace(',', '.');   // EU: 1.234,56 -> 1234.56
        } else {
            s = s.replace(/,/g, '');                       // US: 1,234.56 -> 1234.56
        }
    } else if (hasComma) {
        const parts = s.split(',');
        if (parts.length === 2 && parts[1].length <= 2) s = s.replace(',', '.');
        else s = s.replace(/,/g, '');
    } else if (hasDot) {
        const parts = s.split('.');
        if (parts.length > 2) {
            const last = parts[parts.length - 1];
            if (last.length <= 2) s = parts.slice(0, -1).join('') + '.' + last;
            else s = parts.join('');
        } else if (parts.length === 2 && parts[1].length === 3) {
            s = s.replace(/\./g, ''); // "1.234" -> 1234 (thousands; menu prices never use 3 decimals)
        }
    }

    const parsed = parseFloat(s);
    return isNaN(parsed) ? null : Math.round(parsed * 100);
}

/** Decimal separator used by a currency/number locale (e.g. it-IT → ',', en-US/en-GB → '.'). */
export function decimalSeparatorForLocale(locale: string): '.' | ',' {
    try {
        const dec = new Intl.NumberFormat(locale).formatToParts(1.1).find(p => p.type === 'decimal')?.value;
        return dec === ',' ? ',' : '.';
    } catch {
        return ',';
    }
}

/**
 * Normalize a display price to the menu's chosen decimal separator ('.' or ','), so all public prices
 * look consistent regardless of how the owner typed them. Single source of truth for the "global
 * separator" decision (today derived from the currency locale). Pure & deterministic.
 *
 * Decimals policy (`showDecimals`, default ON — "padding, never rounds"):
 *  - ON  → every price is padded to exactly 2 decimals. "12" -> "12,00", "12,5" -> "12,50".
 *  - OFF → whole prices drop the decimals, prices with real cents are kept INTACT (never rounded).
 *          "12" -> "12", "12,00" -> "12", "12,50" -> "12,50".
 * The integer part is taken from price_cents (the math source of truth); no thousands grouping is added.
 * On unparseable input, returns the original string unchanged (never throws, never blanks a price).
 *   "5.50" + ',' -> "5,50" · "5,50" + '.' -> "5.50" · "12" + ',' (ON) -> "12,00" · "12" + ',' (OFF) -> "12"
 */
export function formatPriceForDisplay(
    priceStr: string | null | undefined,
    decimalSeparator: '.' | ',',
    showDecimals = true
): string {
    if (priceStr === null || priceStr === undefined) return '';
    const raw = String(priceStr).trim();
    if (raw === '') return '';

    const cents = priceToCents(raw);
    if (cents === null) return raw; // unparseable → leave as typed

    const sign = cents < 0 ? '-' : '';
    const abs = Math.abs(cents);
    const whole = Math.trunc(abs / 100);
    const fracValue = abs % 100;

    // OFF: drop decimals only when there are none; real cents are always preserved (never rounded).
    if (!showDecimals && fracValue === 0) return `${sign}${whole}`;

    const frac = String(fracValue).padStart(2, '0');
    return `${sign}${whole}${decimalSeparator}${frac}`;
}

/** Returns true when a price string represents zero (empty, "0", "0.00", "0,00"). */
export function isZeroPrice(price: string | null | undefined): boolean {
    if (!price || price.trim() === '') return true;
    const cents = priceToCents(price);
    return cents === null || cents === 0;
}
